import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { MessageModule } from 'primeng/message';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ChartOfAccounts } from '../../../chart-of-accounts/chart-of-accounts.model';
import { ChartOfAccountsService } from '../../../chart-of-accounts/chart-of-accounts.service';
import { app_permissions } from '../../../security/app-permissions';
import { PermissionService } from '../../../security/permission.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { AccountingTabLabelComponent } from '../../shared/accounting-tab-label.component';
import { BulkCost, BulkCostLine } from './bulk-cost.model';
import { BulkCostService } from './bulk-cost.service';

@Component({
  selector: 'app-bulk-cost-detail',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    MessageModule,
    ProgressBarModule,
    TabsModule,
    TagModule,
    ToastModule,
    AccountingTabLabelComponent,
    PageDataPanelComponent,
    PageDetailHeroComponent,
    PageShellComponent
  ],
  providers: [MessageService],
  templateUrl: './bulk-cost-detail.component.html',
  styleUrls: ['../../finance-list.shared.css', './bulk-cost-detail.component.css']
})
export class BulkCostDetailComponent implements OnInit {
  bulkCost?: BulkCost;
  isLoading = true;
  isActioning = false;
  activeTab = 'overview';
  private readonly accounts = new Map<number, ChartOfAccounts>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly service: BulkCostService,
    private readonly chartOfAccountsService: ChartOfAccountsService,
    private readonly sessionStorage: SessionStorageService,
    private readonly messageService: MessageService,
    private readonly permissions: PermissionService
  ) {}

  ngOnInit(): void {
    this.activeTab = this.route.snapshot.queryParamMap.get('tab') || 'overview';
    if (this.activeTab === 'activity') this.activeTab = 'overview';
    this.route.queryParamMap.subscribe(params => {
      this.activeTab = params.get('tab') || 'overview';
      if (this.activeTab === 'activity') this.activeTab = 'overview';
    });
    this.route.paramMap.subscribe(params => {
      const id = Number(params.get('id') || 0);
      if (!id) {
        this.back();
        return;
      }
      this.load(id);
    });
  }

  get lineCount(): number {
    return this.bulkCost?.lines?.length || 0;
  }

  get editable(): boolean {
    return this.canEdit && !!this.bulkCost?.bulkCostId && this.bulkCost.postingStatus !== 'POSTED'
      && this.bulkCost.status !== 'CANCELLED' && !this.isSisterTruckCost;
  }

  get canApprove(): boolean {
    return this.editable && this.bulkCost?.status === 'UNAPPROVED';
  }

  get canPost(): boolean {
    return this.canPostBulkCost && this.editable && this.bulkCost?.status === 'APPROVED'
      && !this.isSisterTruckCost;
  }

  get isSisterTruckCost(): boolean {
    return this.bulkCost?.sourceType === 'SHIPMENT_INTERNAL_COST'
      && !!this.bulkCost.supplierPartyId && !!this.bulkCost.deliveryId;
  }

  get canEdit(): boolean {
    return this.permissions.hasPermission(app_permissions.accounts.bill.edit);
  }

  get canPostBulkCost(): boolean {
    return this.permissions.hasPermission(app_permissions.accounts.bill.post);
  }

  back(): void {
    this.router.navigate(['/accounts/enterprise/payables/bulk-costs']);
  }

  edit(): void {
    if (this.bulkCost?.bulkCostId) {
      this.router.navigate(['/accounts/enterprise/payables/bulk-costs', this.bulkCost.bulkCostId, 'edit']);
    }
  }

  openJournal(): void {
    if (this.bulkCost?.journalEntryId) {
      this.router.navigate(['/accounts/enterprise/journal-entries/view', this.bulkCost.journalEntryId]);
    }
  }

  approve(): void {
    this.runAction('approve', 'approved');
  }

  post(): void {
    if (!this.bulkCost?.payableAccountId) {
      this.messageService.add({ severity: 'warn', summary: 'Payable account required', detail: 'Set the payable or clearing account before posting this bulk cost.' });
      return;
    }
    this.runAction('post', 'posted');
  }

  cancel(): void {
    this.runAction('cancel', 'cancelled');
  }

  onTabChange(tab: string | number | undefined): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab || 'overview' },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  statusSeverity(status?: string): 'success' | 'warn' | 'danger' | 'info' | 'secondary' {
    switch (status) {
      case 'POSTED': return 'success';
      case 'APPROVED': return 'info';
      case 'CANCELLED': return 'danger';
      case 'EXCEPTION': return 'warn';
      default: return 'secondary';
    }
  }

  formatMoney(value?: number): string {
    const currency = this.bulkCost?.currencyCode || 'SAR';
    return `${currency} ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
  }

  lineAmount(line: BulkCostLine): number {
    return Number(line.amount || 0) || Number(line.quantity || 0) * Number(line.price || 0);
  }

  lineLocalAmount(line: BulkCostLine): number {
    return Number(line.localAmount || 0) || this.lineAmount(line) * Number(this.bulkCost?.exchangeRate || 1);
  }

  lineProjectNumber(line: BulkCostLine): string {
    return line.logisticsProjectNumber || this.bulkCost?.logisticsProjectNumber || 'Not assigned';
  }

  projectContextLabel(cost?: BulkCost, fallbackForMultiple = 'Multiple projects on cost lines'): string {
    if (cost?.logisticsProjectNumber) {
      return cost.logisticsProjectNumber;
    }
    const hasLineProjects = (cost?.lines || []).some(line => !!line.logisticsProjectNumber || !!line.logisticsProjectId);
    return hasLineProjects ? fallbackForMultiple : 'Not assigned';
  }

  accountLabel(accountId?: number): string {
    if (!accountId) return 'Not set';
    const account = this.accounts.get(accountId);
    return account ? [account.accountCode, account.accountName].filter(Boolean).join(' - ') : 'Account unavailable';
  }

  get isPercentageAllocation(): boolean {
    return this.bulkCost?.allocationMode === 'PERCENTAGE';
  }

  allocationPercentageTotal(): number {
    return (this.bulkCost?.lines || []).reduce((total, line) => total + Number(line.allocationPercentage || 0), 0);
  }

  formatLabel(value?: string): string {
    return value ? value.replace(/_/g, ' ') : '-';
  }

  private load(id: number): void {
    this.isLoading = true;
    this.service.get(id).subscribe({
      next: bulkCost => {
        this.bulkCost = bulkCost;
        this.loadAccountLabels(bulkCost);
        this.isLoading = false;
      },
      error: error => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Bulk cost', detail: error?.error?.message || 'Could not load this bulk cost.' });
      }
    });
  }

  private loadAccountLabels(bulkCost: BulkCost): void {
    const companyId = bulkCost.owningCompanyId || this.sessionStorage.getCompany()?.id;
    if (!companyId) {
      this.accounts.clear();
      return;
    }
    this.chartOfAccountsService.getAllChartOfAccountsByCompany(companyId, false).subscribe({
      next: accounts => {
        this.accounts.clear();
        accounts.forEach(account => this.accounts.set(account.accountId, account));
      },
      error: () => this.accounts.clear()
    });
  }

  private runAction(action: 'approve' | 'post' | 'cancel', completed: string): void {
    const bulkCost = this.bulkCost;
    if (!bulkCost?.bulkCostId) return;
    this.isActioning = true;
    this.service[action](bulkCost.bulkCostId, {
      actionDate: new Date(),
      payableAccountId: bulkCost.payableAccountId
    }).subscribe({
      next: updated => {
        this.bulkCost = updated;
        this.isActioning = false;
        this.messageService.add({ severity: 'success', summary: 'Bulk cost', detail: `${updated.bulkCostNumber || 'Bulk cost'} ${completed}.` });
      },
      error: error => {
        this.isActioning = false;
        this.messageService.add({ severity: 'error', summary: 'Bulk cost action failed', detail: error?.error?.message || 'Could not update this bulk cost.' });
      }
    });
  }
}
